<script setup>
import { ref, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';
import { useAlert } from 'dashboard/composables';
import AgenteIaAPI from 'dashboard/api/agenteIa';
import Button from 'dashboard/components-next/button/Button.vue';
import AgenteIaLayout from '../components/AgenteIaLayout.vue';

const { t } = useI18n();
const route = useRoute();
const FILTROS = ['', 'open', 'pending', 'resolved'];

const filtro = ref('');
const conversas = ref([]);
const proxima = ref(null);
const custo = ref(null);
const carregando = ref(true);

const moeda = valor =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL', maximumFractionDigits: 4 }).format(valor || 0);
const numero = valor => new Intl.NumberFormat().format(valor || 0);

const buscar = async (cursor = null) => {
  const { data } = await AgenteIaAPI.conversas({ status: filtro.value || undefined, cursor: cursor || undefined });
  conversas.value = cursor ? [...conversas.value, ...data.payload] : data.payload;
  proxima.value = data.next_cursor;
};

onMounted(async () => {
  try {
    const [{ data }] = await Promise.all([AgenteIaAPI.custo(), buscar()]);
    custo.value = data;
  } catch (e) {
    useAlert(e?.response?.data?.error || t('AGENTE_IA.CONVERSAS.ERRO'));
  } finally {
    carregando.value = false;
  }
});

const filtrar = async valor => {
  filtro.value = valor;
  await buscar();
};

const devolver = async conversa => {
  try {
    await AgenteIaAPI.devolverConversa(conversa.id);
    useAlert(t('AGENTE_IA.CONVERSAS.DEVOLVIDA'));
    await buscar();
  } catch (e) {
    useAlert(e?.response?.data?.error || t('AGENTE_IA.CONVERSAS.ERRO'));
  }
};

const linkDaConversa = conversa =>
  `/app/accounts/${route.params.accountId}/conversations/${conversa.display_id}`;
</script>

<template>
  <AgenteIaLayout
    :title="t('AGENTE_IA.CONVERSAS.TITULO')"
    :description="t('AGENTE_IA.CONVERSAS.DESCRICAO')"
  >
    <p v-if="carregando" class="text-sm text-n-slate-11">
      {{ t('AGENTE_IA.CARREGANDO') }}
    </p>
    <template v-else>
      <section v-if="custo" class="flex flex-col gap-2 p-4 rounded-lg bg-n-alpha-black2">
        <h2 class="text-sm font-medium text-n-slate-12">
          {{ t('AGENTE_IA.CONVERSAS.CUSTO.TITULO', { mes: custo.mes }) }}
        </h2>
        <p class="text-sm text-n-slate-12">
          {{
            t('AGENTE_IA.CONVERSAS.CUSTO.TOTAL', {
              custo: moeda(custo.total.custo_estimado),
              entrada: numero(custo.total.tokens_entrada),
              saida: numero(custo.total.tokens_saida),
            })
          }}
        </p>
        <ul class="flex flex-col gap-0.5 text-xs text-n-slate-11">
          <li v-for="dia in custo.por_dia" :key="dia.dia">
            {{ dia.dia }} · {{ moeda(dia.custo_estimado) }} ·
            {{ numero(dia.tokens_entrada + dia.tokens_saida) }} tokens
          </li>
        </ul>
      </section>

      <div class="flex flex-wrap gap-2">
        <Button
          v-for="valor in FILTROS"
          :key="valor"
          size="sm"
          :variant="filtro === valor ? 'solid' : 'outline'"
          :label="t(`AGENTE_IA.CONVERSAS.FILTROS.${valor ? valor.toUpperCase() : 'TODAS'}`)"
          @click="filtrar(valor)"
        />
      </div>

      <p v-if="!conversas.length" class="text-sm text-n-slate-11">
        {{ t('AGENTE_IA.CONVERSAS.VAZIO') }}
      </p>
      <ul class="flex flex-col gap-2">
        <li
          v-for="conversa in conversas"
          :key="conversa.id"
          class="flex flex-wrap items-center justify-between gap-3 p-3 rounded-lg border border-n-weak"
        >
          <div class="flex flex-col gap-0.5">
            <a
              v-if="!conversa.apagada"
              :href="linkDaConversa(conversa)"
              class="text-sm font-medium text-n-brand"
            >
              #{{ conversa.display_id }} · {{ conversa.contato || t('AGENTE_IA.CONVERSAS.SEM_NOME') }}
            </a>
            <span v-else class="text-sm font-medium text-n-slate-11">
              {{ t('AGENTE_IA.CONVERSAS.APAGADA') }}
            </span>
            <span class="text-xs text-n-slate-11">
              {{ conversa.caixa }}
              <template v-if="!conversa.apagada">
                ·
                {{
                  conversa.com_humano
                    ? t('AGENTE_IA.CONVERSAS.COM_HUMANO', { nome: conversa.humano })
                    : t('AGENTE_IA.CONVERSAS.COM_AGENTE', { nome: conversa.persona })
                }}
              </template>
              · {{ moeda(conversa.custo_estimado) }}
            </span>
            <span v-if="conversa.ultimo_erro" class="text-xs text-n-ruby-11">
              {{ conversa.ultimo_erro }}
            </span>
          </div>
          <Button
            v-if="conversa.com_humano && !conversa.apagada"
            size="sm"
            variant="outline"
            :label="t('AGENTE_IA.CONVERSAS.DEVOLVER')"
            @click="devolver(conversa)"
          />
        </li>
      </ul>
      <Button
        v-if="proxima"
        variant="ghost"
        size="sm"
        :label="t('AGENTE_IA.CONVERSAS.MAIS')"
        @click="buscar(proxima)"
      />
    </template>
  </AgenteIaLayout>
</template>
